import React from 'react';
import { FieldRow, FieldSet, SelectField, TextAreaField, TextField } from './AdmissionFields';
import { DocumentPicker, ProfileImagePicker } from './AdmissionFiles';
import { GENDERS, PARENT_RELATIONSHIPS, formatFileSize } from '../../../utils/admissionValidation';

// The five filled-in steps of the application. Each receives the whole form
// state and reports changes upward; none of them talk to the API.

export const ADMISSION_STEPS = [
  { id: 'student', title: 'Student', heading: 'About the student' },
  { id: 'medical', title: 'Medical', heading: 'Medical and special needs' },
  { id: 'parents', title: 'Parents', heading: 'Parent or guardian' },
  { id: 'details', title: 'Documents', heading: 'Documents and notes' },
  { id: 'review', title: 'Review', heading: 'Review your application' },
];

export const StudentStep = ({ values, errors, setValue, files, setFiles }) => (
  <>
    <ProfileImagePicker
      file={files.profileImage}
      previewUrl={files.profileImagePreview}
      onSelect={(file) => setFiles.setProfileImage(file)}
      onRemove={() => setFiles.setProfileImage(null)}
    />

    <TextField
      id="studentName"
      label="Student's full name"
      value={values.studentName}
      onChange={(v) => setValue('studentName', v)}
      error={errors.studentName}
      autoComplete="name"
      required
    />

    <FieldRow>
      <SelectField
        id="gender"
        label="Gender"
        value={values.gender}
        onChange={(v) => setValue('gender', v)}
        options={GENDERS}
        error={errors.gender}
      />
      <TextField
        id="dateOfBirth"
        label="Date of birth"
        type="date"
        value={values.dateOfBirth}
        onChange={(v) => setValue('dateOfBirth', v)}
        error={errors.dateOfBirth}
        optional
        max={new Date().toISOString().slice(0, 10)}
      />
    </FieldRow>

    <FieldRow>
      <TextField
        id="nationality"
        label="Nationality"
        value={values.nationality}
        onChange={(v) => setValue('nationality', v)}
        optional
      />
      <TextField
        id="preferredStartDate"
        label="Preferred start date"
        type="date"
        value={values.preferredStartDate}
        onChange={(v) => setValue('preferredStartDate', v)}
        optional
      />
    </FieldRow>

    <TextField
      id="address"
      label="Home address"
      value={values.address}
      onChange={(v) => setValue('address', v)}
      optional
      autoComplete="street-address"
    />

    <TextField
      id="previousSchool"
      label="Previous school"
      value={values.previousSchool}
      onChange={(v) => setValue('previousSchool', v)}
      hint="Leave blank if this is the student's first school."
      optional
    />
  </>
);

export const MedicalStep = ({ values, errors, setValue }) => (
  <>
    <p className="ad-step-note">
      Anything here helps the school care for your child properly. Leave a box blank if it does not
      apply.
    </p>

    <TextAreaField
      id="medicalConditions"
      label="Medical conditions"
      value={values.medicalConditions}
      onChange={(v) => setValue('medicalConditions', v)}
      error={errors.medicalConditions}
      hint="Asthma, epilepsy, ongoing medication and so on."
      optional
    />

    <TextAreaField
      id="allergens"
      label="Allergies"
      value={values.allergens}
      onChange={(v) => setValue('allergens', v)}
      error={errors.allergens}
      hint="Food, medicine or anything else the school should keep away from your child."
      optional
    />

    <TextAreaField
      id="specialNeeds"
      label="Special needs"
      value={values.specialNeeds}
      onChange={(v) => setValue('specialNeeds', v)}
      error={errors.specialNeeds}
      hint="Learning support, mobility or any other help your child needs during the school day."
      optional
    />
  </>
);

const ParentFields = ({ prefix, values, errors, setValue, keys }) => (
  <>
    <TextField
      id={keys.name}
      label="Full name"
      value={values[keys.name]}
      onChange={(v) => setValue(keys.name, v)}
      error={errors[keys.name]}
      autoComplete={prefix === 'parent' ? 'name' : 'off'}
    />

    <FieldRow>
      <SelectField
        id={keys.relationship}
        label="Relationship to the student"
        value={values[keys.relationship]}
        onChange={(v) => setValue(keys.relationship, v)}
        options={PARENT_RELATIONSHIPS}
        error={errors[keys.relationship]}
      />
      <TextField
        id={keys.phone}
        label="Phone number"
        type="tel"
        value={values[keys.phone]}
        onChange={(v) => setValue(keys.phone, v)}
        error={errors[keys.phone]}
        hint="Include the country code, e.g. +233 24 123 4567."
        optional={prefix !== 'parent'}
        autoComplete="tel"
        inputMode="tel"
      />
    </FieldRow>

    <FieldRow>
      <TextField
        id={keys.email}
        label="Email address"
        type="email"
        value={values[keys.email]}
        onChange={(v) => setValue(keys.email, v)}
        error={errors[keys.email]}
        optional
        autoComplete="email"
        inputMode="email"
      />
      <TextField
        id={keys.occupation}
        label="Occupation"
        value={values[keys.occupation]}
        onChange={(v) => setValue(keys.occupation, v)}
        optional
      />
    </FieldRow>
  </>
);

export const ParentsStep = ({ values, errors, setValue }) => (
  <>
    <p className="ad-step-note">
      The school uses these details to contact you about the application, so please check the phone
      number is one you answer.
    </p>

    <FieldSet title="Parent or guardian">
      <ParentFields
        prefix="parent"
        values={values}
        errors={errors}
        setValue={setValue}
        keys={{
          name: 'parentName',
          relationship: 'parentRelationship',
          phone: 'parentPhone',
          email: 'parentEmail',
          occupation: 'parentOccupation',
        }}
      />
    </FieldSet>

    {values.hasSecondParent ? (
      <FieldSet title="Second parent or guardian">
        <ParentFields
          prefix="parentTwo"
          values={values}
          errors={errors}
          setValue={setValue}
          keys={{
            name: 'parentTwoName',
            relationship: 'parentTwoRelationship',
            phone: 'parentTwoPhone',
            email: 'parentTwoEmail',
            occupation: 'parentTwoOccupation',
          }}
        />
        <button
          type="button"
          className="ad-text-button"
          onClick={() => setValue('hasSecondParent', false)}
        >
          Remove second parent or guardian
        </button>
      </FieldSet>
    ) : (
      <button
        type="button"
        className="sp-btn sp-btn-outline ad-btn-small"
        onClick={() => setValue('hasSecondParent', true)}
      >
        Add a second parent or guardian
      </button>
    )}
  </>
);

export const DetailsStep = ({ values, errors, setValue, files, setFiles }) => (
  <>
    <DocumentPicker
      documents={files.documents}
      onAdd={setFiles.addDocuments}
      onRemove={setFiles.removeDocument}
    />

    <TextAreaField
      id="admissionNote"
      label="Anything else the school should know"
      value={values.admissionNote}
      onChange={(v) => setValue('admissionNote', v)}
      error={errors.admissionNote}
      optional
      rows={5}
    />
  </>
);

const SummaryRow = ({ label, value }) =>
  value ? (
    <div className="ad-summary-row">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  ) : null;

export const ReviewStep = ({ values, files, schoolName, onEditStep }) => {
  const secondParent = values.hasSecondParent;

  return (
    <>
      <p className="ad-step-note">
        Check the details below, then submit. {schoolName} will contact you using the phone number
        you gave.
      </p>

      <section className="ad-summary" aria-labelledby="ad-summary-student">
        <div className="ad-summary-head">
          <h3 className="ad-summary-title" id="ad-summary-student">
            Student
          </h3>
          <button type="button" className="ad-text-button" onClick={() => onEditStep('student')}>
            Edit
          </button>
        </div>
        <dl className="ad-summary-list">
          <SummaryRow label="Name" value={values.studentName} />
          <SummaryRow label="Gender" value={values.gender} />
          <SummaryRow label="Date of birth" value={values.dateOfBirth} />
          <SummaryRow label="Nationality" value={values.nationality} />
          <SummaryRow label="Home address" value={values.address} />
          <SummaryRow label="Previous school" value={values.previousSchool} />
          <SummaryRow label="Preferred start date" value={values.preferredStartDate} />
          <SummaryRow
            label="Photo"
            value={files.profileImage ? files.profileImage.name : 'Not attached'}
          />
        </dl>
      </section>

      <section className="ad-summary" aria-labelledby="ad-summary-medical">
        <div className="ad-summary-head">
          <h3 className="ad-summary-title" id="ad-summary-medical">
            Medical
          </h3>
          <button type="button" className="ad-text-button" onClick={() => onEditStep('medical')}>
            Edit
          </button>
        </div>
        <dl className="ad-summary-list">
          <SummaryRow label="Medical conditions" value={values.medicalConditions} />
          <SummaryRow label="Allergies" value={values.allergens} />
          <SummaryRow label="Special needs" value={values.specialNeeds} />
          {!values.medicalConditions && !values.allergens && !values.specialNeeds && (
            <SummaryRow label="Medical" value="Nothing to report" />
          )}
        </dl>
      </section>

      <section className="ad-summary" aria-labelledby="ad-summary-parents">
        <div className="ad-summary-head">
          <h3 className="ad-summary-title" id="ad-summary-parents">
            Parent or guardian
          </h3>
          <button type="button" className="ad-text-button" onClick={() => onEditStep('parents')}>
            Edit
          </button>
        </div>
        <dl className="ad-summary-list">
          <SummaryRow label="Name" value={values.parentName} />
          <SummaryRow label="Relationship" value={values.parentRelationship} />
          <SummaryRow label="Phone" value={values.parentPhone} />
          <SummaryRow label="Email" value={values.parentEmail} />
          <SummaryRow label="Occupation" value={values.parentOccupation} />
          {secondParent && <SummaryRow label="Second parent" value={values.parentTwoName} />}
          {secondParent && (
            <SummaryRow label="Second relationship" value={values.parentTwoRelationship} />
          )}
          {secondParent && <SummaryRow label="Second phone" value={values.parentTwoPhone} />}
          {secondParent && <SummaryRow label="Second email" value={values.parentTwoEmail} />}
        </dl>
      </section>

      <section className="ad-summary" aria-labelledby="ad-summary-documents">
        <div className="ad-summary-head">
          <h3 className="ad-summary-title" id="ad-summary-documents">
            Documents and notes
          </h3>
          <button type="button" className="ad-text-button" onClick={() => onEditStep('details')}>
            Edit
          </button>
        </div>
        <dl className="ad-summary-list">
          {files.documents.length === 0 ? (
            <SummaryRow label="Documents" value="None attached" />
          ) : (
            files.documents.map(({ id, file }) => (
              <SummaryRow
                key={id}
                label="Document"
                value={`${file.name} · ${formatFileSize(file.size)}`}
              />
            ))
          )}
          <SummaryRow label="Notes" value={values.admissionNote} />
        </dl>
      </section>
    </>
  );
};
